/**
 * consentVersions
 * ---------------
 * Which version of the consent wording a person actually agreed to
 * (TDI-480; the commitment Jasmine Murphy gave BTH information governance on
 * 25 August 2026: every consent event logged with the time, the person and
 * the version of the statement they agreed).
 *
 * A consent record that says only "they agreed" cannot answer the question
 * that matters a year later — agreed to WHAT? These ids pin each consent to
 * the wording that was on screen when it was given.
 *
 * **Bump the id whenever the wording changes.** The id travels with the
 * consent record forever, so an id that silently covers two different texts
 * destroys the very thing it exists for. Changing a word? New id. The id is
 * dated so the order is obvious to a human reading the audit trail.
 *
 * Mirrors python-functions/constants/rms_consent_versions.py — keep in sync
 * (a parity test pins the two files to each other). The server validates
 * against the same list, so an unknown id is never stored.
 */

/**
 * The family's consent to a referral — the design's five-part consent, one
 * shared text for ADHD, autism and combined referrals (FamilyConsentPage,
 * referralCopy). Only the "For:" line varies, which is not part of the
 * consent itself.
 */
export const FAMILY_REFERRAL_CONSENT_VERSION = 'family-referral-2026-10-05';

/**
 * The family's consent to the SEND pathway (FamilyConsentPage, senCopy).
 */
export const FAMILY_SEN_CONSENT_VERSION = 'family-sen-2026-10-05';

/**
 * The school's confirmation, at referral start, that a person with parental
 * responsibility has consented (HarbourAddToReferralFlyout, CONSENT_OPTIONS
 * and the question above them).
 */
export const SCHOOL_REFERRAL_CONSENT_VERSION = 'school-referral-2026-10-05';

/**
 * The school's confirmation, at SEND pathway start, that the parent or carer
 * has agreed (HarbourAddToSenFlyout, CONSENT_OPTIONS). The wording is under
 * TDI-55; bump this id when that sign-off changes it.
 */
export const SCHOOL_SEN_CONSENT_VERSION = 'school-sen-2026-10-05';

/** Every known id, for validation on both sides. */
export const CONSENT_VERSIONS = [
  FAMILY_REFERRAL_CONSENT_VERSION,
  FAMILY_SEN_CONSENT_VERSION,
  SCHOOL_REFERRAL_CONSENT_VERSION,
  SCHOOL_SEN_CONSENT_VERSION,
];

/**
 * What a consent record written before TDI-480 reads as. Nothing is ever
 * back-filled with a guess: an old record genuinely does not know which
 * wording was shown, and saying so is the only honest answer.
 */
export const CONSENT_VERSION_NOT_RECORDED = 'Version not recorded';

/**
 * The version id to show for a stored consent record.
 * @param {object|null|undefined} consent - a consent or guardianConsent entry
 * @returns {string} the recorded id, or the not-recorded label
 */
export function consentVersionLabel(consent) {
  const version = consent && consent.consentVersion;
  return version || CONSENT_VERSION_NOT_RECORDED;
}

/**
 * The family consent version for a pathway, as the consent screen records it.
 * @param {'sen'|'referral'} pathway
 * @returns {string|null}
 */
export function familyConsentVersion(pathway) {
  if (pathway === 'sen') return FAMILY_SEN_CONSENT_VERSION;
  if (pathway === 'referral') return FAMILY_REFERRAL_CONSENT_VERSION;
  return null;
}
